import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useConversations,
  useMessages,
  useWebSocket,
  useTypingIndicator,
  useMarkConversationRead,
  useAuth,
  type Conversation,
} from '@theraply/api-client';
import {
  ChatWindow,
  ConversationList,
  Spinner,
} from '@theraply/ui';

export default function MessagesPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedConversation, setSelectedConversation] = useState<Conversation | undefined>();

  // Get conversation ID from URL
  const conversationIdParam = searchParams.get('id');
  const conversationId = conversationIdParam ? parseInt(conversationIdParam, 10) : undefined;

  // Fetch conversations
  const { data: conversationsData, isLoading: isLoadingConversations } = useConversations({
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch messages for selected conversation
  const { data: messagesData, isLoading: isLoadingMessages } = useMessages(
    conversationId ?? 0,
    { limit: 50 },
    { enabled: !!conversationId }
  );

  // WebSocket connection
  const { isConnected, sendMessage } = useWebSocket();

  // Typing indicator
  const { isTyping, typingUsers, startTyping, stopTyping } = useTypingIndicator(
    conversationId ?? 0
  );

  // Mark conversation as read
  const { mutate: markConversationRead } = useMarkConversationRead(conversationId ?? 0);

  // Update selected conversation when conversations load or URL changes
  useEffect(() => {
    if (conversationsData?.conversations && conversationId) {
      const conversation = conversationsData.conversations.find((c) => c.id === conversationId);
      setSelectedConversation(conversation);
    }
  }, [conversationsData, conversationId]);

  // Mark conversation as read when selected
  useEffect(() => {
    if (conversationId && selectedConversation) {
      markConversationRead();
    }
  }, [conversationId, selectedConversation, markConversationRead]);

  // Handle conversation selection
  const handleConversationClick = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setSearchParams({ id: conversation.id.toString() });
  };

  // Handle send message
  const handleSendMessage = (content: string) => {
    if (!conversationId) return;

    if (isConnected) {
      sendMessage(conversationId, content);
    } else {
      console.warn('WebSocket not connected, message may not be sent in real-time');
      // Could fallback to REST API here
    }
  };

  // Get typing user name
  const typingUserName = typingUsers.length > 0
    ? selectedConversation?.otherParticipantName
    : undefined;

  return (
    <div className="h-[calc(100vh-4rem)]">
      {/* Page header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Messages</h1>
            <p className="mt-1 text-sm text-gray-500">
              Chat with your clients
            </p>
          </div>

          {/* Connection status */}
          <div className="flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full ${
                isConnected ? 'bg-green-500' : 'bg-gray-400'
              }`}
            />
            <span className="text-sm text-gray-600">
              {isConnected ? 'Connected' : 'Connecting...'}
            </span>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex h-[calc(100%-5rem)]">
        {/* Conversations sidebar */}
        <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto">
          {isLoadingConversations ? (
            <div className="flex items-center justify-center h-full">
              <Spinner size="lg" />
            </div>
          ) : (
            <ConversationList
              conversations={conversationsData?.conversations || []}
              activeConversationId={conversationId}
              onConversationClick={handleConversationClick}
            />
          )}
        </div>

        {/* Chat window */}
        <div className="flex-1">
          <ChatWindow
            conversation={selectedConversation}
            messages={messagesData?.messages || []}
            currentUserId={user?.id ?? 0}
            currentUserType="THERAPIST"
            isLoading={isLoadingMessages}
            isTyping={isTyping}
            typingUserName={typingUserName}
            onSendMessage={handleSendMessage}
            onTypingStart={startTyping}
            onTypingStop={stopTyping}
          />
        </div>
      </div>
    </div>
  );
}
